import type { Planet } from '../lib/api';

/**
 * South Indian chart: signs sit in fixed squares (Pisces top-left, clockwise)
 * and the planets move through them, so the grid reads the same for everyone.
 */
const PLACEMENT: Record<string, [number, number]> = {
  Pisces: [1, 1],
  Aries: [2, 1],
  Taurus: [3, 1],
  Gemini: [4, 1],
  Cancer: [4, 2],
  Leo: [4, 3],
  Virgo: [4, 4],
  Libra: [3, 4],
  Scorpio: [2, 4],
  Sagittarius: [1, 4],
  Capricorn: [1, 3],
  Aquarius: [1, 2],
};

const ABBR: Record<string, string> = {
  Sun: 'Su',
  Moon: 'Mo',
  Mercury: 'Me',
  Venus: 'Ve',
  Mars: 'Ma',
  Jupiter: 'Ju',
  Saturn: 'Sa',
  Rahu: 'Ra',
  Ketu: 'Ke',
};

export function SouthIndianChart({
  planets,
  ascendant,
}: {
  planets: Planet[];
  ascendant: string | null;
}) {
  return (
    <div
      className="grid aspect-square w-full grid-cols-4 grid-rows-4 border-l border-t border-[rgba(16,32,26,0.18)]"
      role="img"
      aria-label={`Birth chart${ascendant ? `, ${ascendant} rising` : ''}`}
    >
      {Object.entries(PLACEMENT).map(([sign, [col, row]]) => {
        const here = planets.filter((p) => p.sign === sign);
        return (
          <div
            key={sign}
            style={{ gridColumn: col, gridRow: row }}
            className="relative flex flex-col justify-between border-b border-r border-[rgba(16,32,26,0.18)] p-1.5"
          >
            <span className="font-mono text-[0.55rem] uppercase tracking-[0.1em] text-sage-deep">
              {sign.slice(0, 3)}
            </span>
            <span className="flex flex-wrap gap-x-1.5 font-mono text-[0.72rem] text-ink">
              {sign === ascendant && <span className="text-sage">As</span>}
              {here.map((p) => (
                <span key={p.name}>
                  {ABBR[p.name] ?? p.name}
                  {p.retrograde && p.name !== 'Rahu' && p.name !== 'Ketu' ? 'ᴿ' : ''}
                </span>
              ))}
            </span>
          </div>
        );
      })}
      <div
        style={{ gridColumn: '2 / span 2', gridRow: '2 / span 2' }}
        className="flex flex-col items-center justify-center border-b border-r border-[rgba(16,32,26,0.18)] text-center"
      >
        <span className="eyebrow">Rasi</span>
        <span className="mt-1 font-display text-lg text-ink">
          {ascendant ? `${ascendant} rising` : 'Moon chart'}
        </span>
      </div>
    </div>
  );
}
